/** @fileoverview Edits and validates an app definition as JSON. */

import { json } from '@codemirror/lang-json';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { tags } from '@lezer/highlight';
import { Braces } from 'lucide-react';
import { Button } from '../ui/button';

const editorTheme = EditorView.theme({
  '&': {
    height: '100%',
    minHeight: '0',
    color: 'var(--app-text)',
    backgroundColor: 'var(--app-surface-muted)',
  },
  '.cm-scroller': { minHeight: '0', overflow: 'auto' },
  '.cm-gutters': {
    color: 'var(--app-text-faint)',
    backgroundColor: 'var(--app-surface-card)',
    borderRight: '1px solid var(--app-border)',
  },
  '.cm-activeLineGutter': { backgroundColor: 'var(--app-surface-muted-strong)' },
  '.cm-activeLine': { backgroundColor: 'var(--app-surface-muted-strong)' },
  '.cm-cursor': { borderLeftColor: 'var(--app-text)' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'var(--app-border-strong) !important',
    color: 'var(--app-text) !important',
  },
  '.cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'var(--app-border-strong) !important',
    color: 'var(--app-text) !important',
  },
});

const editorHighlighting = syntaxHighlighting(
  HighlightStyle.define([
    { tag: tags.propertyName, color: 'var(--app-branch)' },
    { tag: tags.string, color: 'var(--app-text-secondary)' },
    { tag: tags.number, color: 'var(--app-accent)' },
    { tag: [tags.bool, tags.null], color: 'var(--app-text-subtle)' },
    { tag: tags.bracket, color: 'var(--app-text-faint)' },
  ])
);

const editorExtensions = [json(), editorTheme, editorHighlighting];

type Props = {
  definition: string;
  jsonError: string;
  saveError: string;
  saveMessage: string;
  saving: boolean;
  onDefinitionChange: (value: string) => void;
  onReset: () => void;
  onSave: () => void;
};

export default function JsonCanvasEditor({
  definition,
  jsonError,
  saveError,
  saveMessage,
  saving,
  onDefinitionChange,
  onReset,
  onSave,
}: Props) {
  return (
    <main className="flex min-h-0 flex-1 flex-col bg-app-surface-muted">
      <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-5 px-6 py-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-app-text">Start from JSON</h1>
            <p className="mt-1 text-sm text-app-text-muted">
              Write the app definition by hand, then save it to create the app.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-app-border bg-app-surface-card text-app-text"
              onClick={onReset}
            >
              Reset JSON
            </Button>
            <Button
              type="button"
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
              disabled={Boolean(jsonError) || saving}
              onClick={onSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {saveMessage && <p className="text-sm text-app-branch">{saveMessage}</p>}

        <div className="flex min-h-0 flex-1">
          <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-xl border border-app-border bg-app-surface-card p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-app-tab-text">
                <Braces className="h-4 w-4" aria-hidden="true" />
                JSON editor
              </div>
              <span className={jsonError ? 'text-xs text-app-warning' : 'text-xs text-app-branch'}>
                {jsonError ? 'Invalid JSON' : 'Valid JSON'}
              </span>
            </div>
            <div className="relative mt-4 min-h-0 flex-1 overflow-hidden rounded-lg border border-app-border bg-app-surface-muted">
              <CodeMirror
                value={definition}
                height="100%"
                theme="none"
                extensions={editorExtensions}
                basicSetup={{ lineNumbers: true, foldGutter: false }}
                onChange={onDefinitionChange}
                className="h-full min-h-0 overflow-hidden text-sm"
                aria-label="App definition JSON"
              />
            </div>
            {(saveError || jsonError) && (
              <div className="border-t border-app-border-soft pt-2">
                {saveError ? (
                  <p className="whitespace-pre-wrap text-xs text-app-danger" role="alert">
                    {saveError}
                  </p>
                ) : (
                  <p className="text-xs text-app-warning" role="alert">
                    {jsonError}
                  </p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
